import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import moment from 'moment';
import { Popup } from 'semantic-ui-react';

// Types
import { AppState } from 'services/types';

// Actions
import { updateQueryString } from 'services/router/actions';

// Global Selectors
import {
    makeSelectHarvestEventStartDate,
    makeSelectHarvestEventEndDate
} from 'services/harvestEvents/selectors';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';
// @ts-ignore
import {
    selectIsManager,
    selectIsAssistantManager,
    selectIsAdmin
} from 'services/usersMe/selectors';

// Local Components
// @ts-ignore
import StyledEventsTimelineEvent from '../../../../../../../StyledEventsTimelineEvent';
// @ts-ignore
import EventsTimelineEventDetails from '../EventsTimelineEventDetails';
// @ts-ignore
import EventsTimelineEventDragSource from './components/EventsTimelineEventDragSource';

// Local Selectors
// @ts-ignore
import { selectFirstDate, selectLastDate } from '../../../../../../selectors';
// @ts-ignore
import { makeSelectIsOpen, selectIsMobile, OwnProps } from './selectors';

// Constants
// @ts-ignore
import { HEIGHT } from '../../../../../../../../constants';

// Styles
import './styles.scss';

interface ConnectedProps {
    firstDate: string;
    lastDate: string;
    startDate: string;
    endDate: string;
    currentSeasonIsComplete?: boolean;
    isManager?: boolean;
    isAdmin?: boolean;
    isAssistantManager?: boolean;
    isOpen?: boolean;
    isMobile: boolean;
}

interface DispatchProps {
    updateQueryString: Function;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class EventsTimelineEvent extends React.PureComponent<Props> {
    popupStyle: { top?: string } = {};

    // TODO: move into events selectors
    get overflowLeft(): boolean {
        const { firstDate, startDate } = this.props;
        return firstDate > startDate;
    }

    get overflowRight(): boolean {
        const { lastDate, endDate } = this.props;
        return lastDate < endDate;
    }

    get firstDisplayDate(): string {
        const { firstDate, startDate } = this.props;
        return this.overflowLeft ? firstDate : startDate;
    }

    get lastDisplayDate(): string {
        const { lastDate, endDate } = this.props;
        return this.overflowRight ? lastDate : endDate;
    }

    get difference(): number {
        const startDate = moment(this.firstDisplayDate, 'YYYY-MM-DD');
        const endDate = moment(this.lastDisplayDate, 'YYYY-MM-DD');
        return endDate.diff(startDate, 'days');
    }

    get draggingDisabled(): boolean {
        const {
            currentSeasonIsComplete,
            isManager,
            isAdmin,
            isAssistantManager,
            isMobile
        } = this.props;

        return (
            currentSeasonIsComplete ||
            isMobile ||
            (!isManager && !isAdmin && !isAssistantManager)
        );
    }

    handleOpen = (): void => {
        const { harvestEventUuid, groupId } = this.props;
        this.props.updateQueryString({
            eventPopup: true,
            selectedEvent: harvestEventUuid,
            selectedGroup: groupId || 'null'
        });
    };

    handleClose = (): void => {
        this.props.updateQueryString({
            eventPopup: undefined,
            selectedEvent: undefined,
            selectedGroup: undefined
        });
    };

    handlePopupPosition = (style: { top?: string | undefined }) => {
        this.popupStyle = style;
    };

    render(): React.ReactElement {
        const { startDate, harvestEventUuid, groupId, isOpen } = this.props;

        if (!startDate) {
            return (
                <div
                    style={{ height: HEIGHT }}
                    className="events-timeline-event"
                />
            );
        }
        const vh = Math.max(
            document.documentElement.clientHeight || 0,
            window.innerHeight || 0
        );
        if (vh <= 380) {
            this.popupStyle = {
                top: '60px'
            };
        }

        const Content = (
            <div style={{ height: HEIGHT }} className="events-timeline-event">
                <div className="events-timeline-event-inner">
                    <Popup
                        trigger={
                            <StyledEventsTimelineEvent
                                width={`${(this.difference + 1) * 100}%`}
                                overflowRight={this.overflowRight}
                                overflowLeft={this.overflowLeft}
                                harvestEventUuid={harvestEventUuid}
                                groupId={groupId}
                                draggingDisabled={this.draggingDisabled}
                            />
                        }
                        content={
                            <EventsTimelineEventDetails
                                harvestEventUuid={harvestEventUuid}
                            />
                        }
                        style={this.popupStyle}
                        basic
                        wide
                        on="click"
                        hideOnScroll
                        position="left center"
                        open={isOpen}
                        onClose={this.handleClose}
                        onOpen={this.handleOpen}
                    />
                </div>
            </div>
        );

        if (this.draggingDisabled) {
            return Content;
        }

        return (
            <EventsTimelineEventDragSource
                harvestEventUuid={harvestEventUuid}
                groupId={groupId}
            >
                {Content}
            </EventsTimelineEventDragSource>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    firstDate: selectFirstDate,
    lastDate: selectLastDate,
    startDate: makeSelectHarvestEventStartDate(),
    endDate: makeSelectHarvestEventEndDate(),
    currentSeasonIsComplete: selectCurrentSeasonIsComplete,
    isOpen: makeSelectIsOpen(),
    isManager: selectIsManager,
    isAdmin: selectIsAdmin,
    isAssistantManager: selectIsAssistantManager,
    isMobile: selectIsMobile
});

export default connect(mapStateToProps, { updateQueryString })(
    EventsTimelineEvent
);
